/**
 * useQzTray.ts
 *
 * QZ Tray(로컬 데몬)와의 WebSocket 연결 및 보안 설정을 담당하는 훅.
 * - 앱 로드 시 HomeContainer에서 한 번 호출되어, 로컬 QZ Tray 앱(localhost:8181/8182)에 연결을 시도한다.
 * - 연결 전에 백엔드 API(/label/certificate, /label/sign)로 인증서·서명을 받아 QZ 보안을 설정한다.
 * - 연결 실패 시 1시간 동안 재시도를 스킵하여 불필요한 요청을 줄인다.
 */
import qz from 'qz-tray';
import { useCallback, useEffect, useRef, useState } from 'react';
import config from 'src/config';
import { getCredentialsOption } from 'src/utils/auth/authConfig';

/** QZ Tray 연결/프린터 상태 (UI 표시 및 버튼 비활성화 등에 사용) */
export interface QzTrayState {
  isConnected: boolean;
  isConnecting: boolean;
  printerList: string[];
  defaultPrinter: string;
  error: string | null;
}

const initialState: QzTrayState = {
  isConnected: false,
  isConnecting: false,
  printerList: [],
  defaultPrinter: '',
  error: null,
};

/** 전역 플래그: connect() 성공 시 true. 여러 컴포넌트에서 동일한 연결 상태 공유용 */
let globalConnected = false;
/** 전역 프린터 목록 (설정 화면 등에서 사용) */
let globalPrinterList: string[] = [];
/** 앱 생애주기 내 보안 설정·연결 시도는 한 번만 수행하기 위한 플래그 */
let globalInitialized = false;
let globalSecuritySetup = false;

/** localStorage 키: 마지막 연결 실패 시각 저장 → 1시간 이내 재시도 스킵 */
const QZ_UNAVAILABLE_KEY = 'qz_unavailable';
const ONE_HOUR = 60 * 60 * 1000;

/**
 * QZ 연결 성공 여부 (connect() 성공 시에만 true).
 * useQzPrint에서 "QZ 없으면 브라우저 인쇄로 대체" 판단 시 사용 (websocket.isActive()만으로는 재시도 스킵 구간 반영 안 됨)
 */
export const getQzConnected = (): boolean => globalConnected;

export const useQzTray = () => {
  const [state, setState] = useState<QzTrayState>({
    ...initialState,
    isConnected: globalConnected,
    printerList: globalPrinterList,
  });

  /** 언마운트 후 setState 호출 방지 (메모리 경고 및 불필요한 업데이트 방지) */
  const isMounted = useRef(true);

  /** 컴포넌트가 마운트된 경우에만 state 갱신 (비동기 완료 시점에 언마운트됐을 수 있음) */
  const safeSetState = useCallback(
    (updater: Partial<QzTrayState> | ((prev: QzTrayState) => Partial<QzTrayState>)) => {
      if (!isMounted.current) return;
      setState(prev => ({
        ...prev,
        ...(typeof updater === 'function' ? updater(prev) : updater),
      }));
    },
    []
  );

  /**
   * QZ Tray 보안 설정 (서명 방식).
   * 연결 전에 한 번만 호출. 백엔드에서 인증서·개인키로 서명을 제공하고, QZ는 이 서명으로 로컬 데몬과 통신을 인증한다.
   */
  const setupSecurity = useCallback(() => {
    console.log('[QZ] setupSecurity 시작, apiUrl:', config.apiUrl);

    qz.security.setSignatureAlgorithm('SHA512');

    /** QZ가 인증서가 필요할 때 호출. 백엔드 GET /label/certificate 로 PEM 문자열 수신 */
    qz.security.setCertificatePromise(
      (resolve: (cert: string) => void, reject: (err: any) => void) => {
        const url = `${config.apiUrl}/label/certificate`;
        console.log('[QZ] /label/certificate 호출 →', url);
        fetch(url, { credentials: getCredentialsOption() })
          .then(res => {
            if (!res.ok) throw new Error(`인증서 조회 실패: ${res.status}`);
            return res.text();
          })
          .then(cert => {
            console.log('[QZ] 인증서 조회 OK, 길이:', cert?.length);
            resolve(cert);
          })
          .catch(err => {
            console.error('[QZ] 인증서 조회 실패:', err?.message ?? err);
            reject(err);
          });
      }
    );

    /** QZ가 서명이 필요할 때 toSign 문자열을 넘김. 백엔드 POST /label/sign 에서 SHA512withRSA 서명 후 Base64 반환 */
    qz.security.setSignaturePromise((toSign: string) => {
      return (resolve: (val: string) => void, reject: (err: any) => void) => {
        const url = `${config.apiUrl}/label/sign`;
        fetch(url, {
          method: 'POST',
          credentials: getCredentialsOption(),
          headers: {
            'Content-Type': 'text/plain',
          },
          body: toSign,
        })
          .then(res => {
            if (!res.ok) throw new Error(`서명 실패: ${res.status}`);
            return res.text();
          })
          .then(signature => {
            console.log('[QZ] 서명 OK');
            resolve(signature);
          })
          .catch(err => {
            console.error('[QZ] 서명 실패:', err?.message ?? err);
            reject(err);
          });
      };
    });
  }, []);

  /**
   * QZ Tray WebSocket 연결.
   * 1) 1시간 이내 실패 이력 있으면 재시도 스킵.
   * 2) 이미 연결돼 있으면 프린터 목록만 갱신 후 true.
   * 3) 그 외 localhost:8181(secure)/8182(insecure) 로 연결 시도 후 프린터 목록 조회.
   */
  const connect = useCallback(async (): Promise<boolean> => {
    const failedAt = Number(localStorage.getItem(QZ_UNAVAILABLE_KEY) || 0);
    if (failedAt && Date.now() - failedAt < ONE_HOUR) {
      console.log('[QzTray] 이전 연결 실패 이력 있음 → 연결 시도 스킵');
      globalConnected = false;
      safeSetState({ isConnected: false, isConnecting: false });
      return false;
    }

    if (qz.websocket.isActive()) {
      const printers = await qz.printers.find();
      const defaultPrinter = await qz.printers.getDefault();
      globalConnected = true;
      globalPrinterList = printers;
      safeSetState({
        isConnected: true,
        isConnecting: false,
        printerList: printers,
        defaultPrinter,
        error: null,
      });
      return true;
    }

    safeSetState({ isConnecting: true, error: null });

    try {
      await qz.websocket.connect({
        retries: 0,
        delay: 1,
        host: ['localhost'],
        port: { secure: [8181], insecure: [8182] },
      });

      localStorage.removeItem(QZ_UNAVAILABLE_KEY);

      const printers: string[] = await qz.printers.find();
      const defaultPrinter: string = await qz.printers.getDefault();

      globalConnected = true;
      globalPrinterList = printers;

      safeSetState({
        isConnected: true,
        isConnecting: false,
        printerList: printers,
        defaultPrinter,
        error: null,
      });

      console.log('[QzTray] 연결 완료, 프린터 목록:', printers);
      return true;

    } catch (err: any) {
      localStorage.setItem(QZ_UNAVAILABLE_KEY, String(Date.now()));
      globalConnected = false;
      safeSetState({
        isConnected: false,
        isConnecting: false,
        error: err?.message || 'QZ Tray 연결 실패',
      });
      console.log('[QzTray] QZ Tray 미연결 (설치되지 않았거나 실행 중이 아님)');
      return false;
    }
  }, [safeSetState]);

  /**
   * 수동 재연결. QZ Tray를 나중에 설치/실행한 뒤 "다시 연결" 버튼 등에서 호출.
   * 실패 이력과 globalInitialized를 초기화한 뒤 connect() 재실행.
   */
  const retryConnect = useCallback(async (): Promise<boolean> => {
    localStorage.removeItem(QZ_UNAVAILABLE_KEY);
    globalInitialized = false;
    return connect();
  }, [connect]);

  /** WebSocket 연결 해제 후 전역 상태·로컬 state 초기화 */
  const disconnect = useCallback(async () => {
    try {
      if (qz.websocket.isActive()) {
        await qz.websocket.disconnect();
      }
      globalConnected = false;
      globalPrinterList = [];
      safeSetState({
        isConnected: false,
        printerList: [],
        defaultPrinter: '',
        error: null,
      });
    } catch (err) {
      console.error('[QzTray] 연결 해제 실패:', err);
    }
  }, [safeSetState]);

  /** 연결된 상태에서 프린터 목록·기본 프린터만 다시 조회하여 state 갱신 (USB 프린터 추가 등 반영) */
  const refreshPrinters = useCallback(async () => {
    if (!qz.websocket.isActive()) return;
    try {
      const printers: string[] = await qz.printers.find();
      const defaultPrinter: string = await qz.printers.getDefault();
      globalPrinterList = printers;
      safeSetState({ printerList: printers, defaultPrinter });
    } catch (err) {
      console.error('[QzTray] 프린터 목록 조회 실패:', err);
    }
  }, [safeSetState]);

  /**
   * 마운트 시 1회: 보안 설정(setupSecurity) 후 connect() 호출.
   * globalSecuritySetup / globalInitialized 로 앱 전체에서 한 번만 실행되도록 함.
   */
  useEffect(() => {
    isMounted.current = true;
    if (!globalSecuritySetup) {
      globalSecuritySetup = true;
      setupSecurity();
    }
    if (!globalInitialized) {
      globalInitialized = true;
      connect();
    }
    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    ...state,
    connect,
    retryConnect,
    disconnect,
    refreshPrinters,
  };
};
